import { createContext, ReactChild, useContext, useEffect, useState } from 'react';

import * as AuthService from '../../api/services/Auth';

interface AuthProviderProps {
  children: ReactChild;
}

interface IAuthContext {
  user?: User | null;
  loading: boolean;
  userIdentity?: Identity;
  authenticityToken: string;
  getUserProfile: () => void;
  handleSessionUnauthorized: () => void;
}

const AuthContext = createContext<IAuthContext | null>(null);

function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userIdentity, setUserIdentity] = useState<Identity>();
  const [authenticityToken, setAuthenticityToken] = useState<string>('');

  useEffect(() => {
    getUserProfile();
    getCsrf();
  }, []);

  const getCsrf = async () => {
    const response = await AuthService.getCsrf();

    if (response.type === 'success') {
      setAuthenticityToken(response.data.authenticityToken);
    }
  };

  const getUserProfile = async () => {
    const response = await AuthService.getUserProfile();

    // change after request to make api not reture 200 when unauthorized
    if (response.type === 'success' && response.data) {
      const { srsAvailability, ...currentUser } = response.data.currentUser;

      setUser({
        ...currentUser,
        srsAvailability: {
          ...srsAvailability,
          status: srsAvailability?.status || 'error',
        },
      });

      setUserIdentity({
        email: response.data.currentUser?.email,
        token: response.data.currentUser?.authToken,
        type: response.data.currentUser?.type,
      });
    } else {
      setUser(null);
    }
    setLoading(false);
  };

  function handleSessionUnauthorized() {
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, userIdentity, authenticityToken, getUserProfile, handleSessionUnauthorized }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth(): IAuthContext {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return authContext;
}

export { AuthProvider, useAuth };
